import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { Check, X, CloudUpload, ExternalLink } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Button } from "./Button";
import { Loader } from "./Loader";
import { Label } from "./Label";
import { Type } from "./Type";
import { FileType, AllowedFileTypes } from "@shared/types/file.types";

export type Props = {
  hash?: string;
  dropzoneMessage?: string;
  acceptedFileTypes?: string;
  allowedFileTypes?: AllowedFileTypes;
  className?: string;
  uploadFileHandler: (
    file: File,
    onProgress: (progress: number) => void
  ) => Promise<Partial<FileType>>;
  onUploadedFilesChange?: (files: FileType[]) => void;
  onAllFilesHandled?: () => void;
};

const ALLOWED_FILE_TYPES: AllowedFileTypes = {
  "image/jpeg": true,
  "image/png": true,
  "image/gif": true,
  "image/svg+xml": true,
};

export function FileUploader({
  hash = "",
  className,
  uploadFileHandler,
  onAllFilesHandled,
  onUploadedFilesChange,
  dropzoneMessage = "Click to upload or drag and drop",
  acceptedFileTypes = "Images (JPG, PNG, GIF, SVG)",
  allowedFileTypes = ALLOWED_FILE_TYPES,
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileType[]>([]);

  const inFlightUploads = useRef(0);

  const checkIfAllHandled = () => {
    if (inFlightUploads.current === 0) {
      onAllFilesHandled?.();
    }
  };

  const updateFiles = (updater: (prev: FileType[]) => FileType[]) => {
    setUploadedFiles((prev) => {
      const next = updater(prev);
      onUploadedFilesChange?.(next);
      return next;
    });
  };
  const validateFile = (file: File): boolean => {
    if (!allowedFileTypes[file.type]) {
      setValidationError(`File type ${file.type} is not supported.`);
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleUpload = async (file: File) => {
    const fileId = Math.random();

    const placeholder: FileType = {
      id: fileId,
      name: file.name,
      size: file.size.toString(),
      progress: 0,
      error: false,
      checked: false,
    };

    inFlightUploads.current += 1;

    updateFiles((prev) => [...prev, placeholder]);

    try {
      const mediaObject = await uploadFileHandler(file, (progress: number) => {
        updateFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, progress } : f))
        );
      });

      updateFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, ...mediaObject, progress: 100 } : f
        )
      );
    } catch (err) {
      updateFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, error: true, progress: 0 } : f
        )
      );
    } finally {
      inFlightUploads.current -= 1;
      checkIfAllHandled();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        if (validateFile(file)) {
          handleUpload(file);
        }
      });
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();

    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files) {
      Array.from(files).forEach((file) => {
        if (validateFile(file)) {
          handleUpload(file);
        }
      });
    }
  };

  const renderFile = (file: FileType, index: number) => (
    <div
      className={twMerge(
        "flex flex-col items-center justify-between rounded-md border p-4 w-full gap-4 md:flex-row md:gap-0",
        file.error ? "border-destructive" : "border-brand-800"
      )}
      key={index}
    >
      <div className="flex flex-col items-center gap-2 md:flex-row">
        <a
          href={file.file}
          target="_blank"
          className="inline-flex items-center"
          data-testid="file-uploader-preview-link"
        >
          <Button
            iconComponent={ExternalLink}
            variant="ghost"
            iconPosition="right"
          >
            {file.name}
          </Button>
        </a>
        <Type variant="caption" className="text-brand-600" as="span">
          {`${(Number(file.size) / 1024).toFixed(2)} KB`} - {file.progress}%
          uploaded
        </Type>
        {file.error && (
          <Type variant="caption" className="text-error" as="a">
            Try again
          </Type>
        )}
      </div>

      <div className="flex flex-shrink-0 w-6 flex-col items-end text-right">
        {file.error ? (
          <X className="h-6 w-6 text-error" />
        ) : file.progress === 100 ? (
          <Check className="h-6 w-6 text-success" />
        ) : (
          <Loader className="ml-2 h-10 w-10" />
        )}
      </div>
    </div>
  );

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 items-center text-center",
        className
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-1 rounded-md border p-4 cursor-pointer w-full transition-all duration-100 hover:shadow-md",
          isDragging && "border-dashed border-brand-600 bg-brand-100"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById(`file-upload${hash}`)?.click()}
      >
        <CloudUpload className="mx-auto text-brand-700" />
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload${hash}`}
          accept=".jpg,.jpeg,.png,.gif,.svg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
        <Label className="cursor-pointer">{dropzoneMessage}</Label>
        <Type variant="caption" className="text-brand-700">
          {acceptedFileTypes}
        </Type>
      </div>
      {validationError && (
        <Type variant="caption" className="text-error">
          {validationError}
        </Type>
      )}
      {uploadedFiles.length > 0 &&
        uploadedFiles.map((file, index) => renderFile(file, index))}
    </div>
  );
}

export default FileUploader;
